import { ChevronsUpDown, Plus, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import ReactSelect, { StylesConfig } from 'react-select'
import { toast } from 'sonner'

import { createTag, getTags, updateProductTags } from '@/api/api'
import type { Tag, TagType } from '@/api/type'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type Props = {
  productId: number
  domain: string
  currentTags: Tag[]
  onTagsUpdated: (tags: Tag[]) => void
}

type TagOption = {
  label: string
  value: number
  tag: Tag
}

const TAG_TYPES: { value: TagType; label: string }[] = [
  { value: 'author', label: 'Author' },
  { value: 'category', label: 'Category' },
  { value: 'genre', label: 'Genre' },
  { value: 'publisher', label: 'Publisher' },
  { value: 'series', label: 'Series' },
  { value: 'tag', label: 'Tag' },
]

export const TagEditor = ({ productId, domain, currentTags, onTagsUpdated }: Props) => {
  const [open, setOpen] = useState(false)
  const [allTags, setAllTags] = useState<Tag[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [tagToRemove, setTagToRemove] = useState<Tag | null>(null)

  // 新規タグ作成用のstate
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagType, setNewTagType] = useState<TagType>('tag')
  const { isSubmitting: isCreating, run } = useSubmitLock()

  // 既に選択済みのタグIDセット
  const selectedTagIds = useMemo(() => new Set(currentTags.map((t) => t.id)), [currentTags])

  // 選択可能なタグ（まだ追加されていないもの）をreact-select用のオプションに変換
  const tagOptions: TagOption[] = useMemo(
    () =>
      allTags
        .filter((t) => !selectedTagIds.has(t.id))
        .map((tag) => ({
          label: `${tag.type}: ${tag.name}`,
          value: tag.id,
          tag,
        })),
    [allTags, selectedTagIds],
  )

  // react-select用のスタイル
  const selectStyles: StylesConfig<TagOption, false> = {
    control: (styles) => ({
      ...styles,
      backgroundColor: 'black',
      borderColor: '#333',
      color: 'white',
      minHeight: '36px',
    }),
    input: (styles) => ({
      ...styles,
      color: 'white',
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: 'black',
      border: '1px solid #333',
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? '#333' : 'black',
      color: 'white',
      ':hover': {
        backgroundColor: '#333',
      },
    }),
    placeholder: (styles) => ({
      ...styles,
      color: '#888',
    }),
    singleValue: (styles) => ({
      ...styles,
      color: 'white',
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      color: '#888',
    }),
  }

  // タグ一覧を取得
  const fetchTags = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await getTags(domain)
      setAllTags(response.tags)
    } catch (error) {
      console.error('Failed to fetch tags:', error)
      toast.error('タグの取得に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [domain])

  // Popoverが開いた時にタグを取得
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (isOpen && allTags.length === 0) {
      fetchTags()
    }
  }

  // タグを追加
  const handleAddTag = async (tag: Tag) => {
    try {
      const response = await updateProductTags(productId, {
        add_tag_ids: [tag.id],
        remove_tag_ids: [],
      })
      onTagsUpdated(response.tags)
      toast.success(`タグ「${tag.name}」を追加しました`)
    } catch (error) {
      console.error('Failed to add tag:', error)
      toast.error('タグの追加に失敗しました')
    }
  }

  // タグを削除（確認後）
  const handleRemoveTag = async () => {
    if (!tagToRemove) return

    try {
      const response = await updateProductTags(productId, {
        add_tag_ids: [],
        remove_tag_ids: [tagToRemove.id],
      })
      onTagsUpdated(response.tags)
      toast.success(`タグ「${tagToRemove.name}」を削除しました`)
    } catch (error) {
      console.error('Failed to remove tag:', error)
      toast.error('タグの削除に失敗しました')
    } finally {
      setTagToRemove(null)
    }
  }

  // 新規タグを作成
  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      toast.error('タグ名を入力してください')
      return
    }

    await run(async () => {
      try {
        const newTag = await createTag({
          name: newTagName.trim(),
          type: newTagType,
          domain,
        })
        // 作成したタグを一覧に追加
        setAllTags((prev) => [...prev, newTag])
        // 作成したタグをプロダクトに追加
        await handleAddTag(newTag)
        // フォームをリセット
        setNewTagName('')
        setNewTagType('tag')
        setShowCreateForm(false)
      } catch (error) {
        console.error('Failed to create tag:', error)
        toast.error('タグの作成に失敗しました')
      }
    })
  }

  return (
    <div className="space-y-3">
      {/* 現在のタグ一覧（削除ボタン付き） */}
      <div className="flex flex-wrap gap-2">
        {currentTags.map((tag) => (
          <Badge key={tag.id} variant="secondary" className="flex items-center gap-1 pr-1!">
            <span className="text-xs text-muted-foreground">{tag.type}:</span>
            {tag.name}
            <button
              type="button"
              onClick={() => setTagToRemove(tag)}
              className="ml-1 rounded-full hover:bg-destructive/20 p-0.5!"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
      </div>

      {/* タグ追加Popover */}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-full justify-between">
            タグを追加
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-75 p-2!" align="start">
          <ReactSelect<TagOption, false>
            options={tagOptions}
            placeholder={isLoading ? '読み込み中...' : 'タグを検索...'}
            noOptionsMessage={() => (isLoading ? '読み込み中...' : 'タグが見つかりません')}
            onChange={(option: TagOption | null) => {
              if (option) {
                handleAddTag(option.tag)
                setOpen(false)
              }
            }}
            value={null}
            styles={selectStyles}
            isLoading={isLoading}
            menuIsOpen
            autoFocus
          />

          {/* 新規タグ作成セクション */}
          <div className="border-t mt-2 pt-2">
            {!showCreateForm ? (
              <Button variant="ghost" size="sm" className="w-full" onClick={() => setShowCreateForm(true)}>
                <Plus className="mr-2 size-4" />
                新しいタグを作成
              </Button>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="タグ名"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleCreateTag()
                    }
                  }}
                />
                <Select value={newTagType} onValueChange={(v) => setNewTagType(v as TagType)}>
                  <SelectTrigger>
                    <SelectValue placeholder="タイプを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {TAG_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setShowCreateForm(false)
                      setNewTagName('')
                    }}
                  >
                    キャンセル
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={handleCreateTag}
                    disabled={isCreating || !newTagName.trim()}
                  >
                    {isCreating ? '作成中...' : '作成'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* 削除確認ダイアログ */}
      <AlertDialog open={!!tagToRemove} onOpenChange={(open) => !open && setTagToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>タグを削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              タグ「{tagToRemove?.name}」をこのプロダクトから削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveTag}>削除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
